import { ActionMenu } from '@monorepo/shared/componentsV2/menus/ActionMenu';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { header2 } from '@monorepo/shared/styles/text';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { DeleteSectionModal } from './DeleteSectionModal';
import { CopySectionModal } from './EditSectionModal/CopySectionModal';
import { EditSectionModal } from './EditSectionModal/EditSectionModal';

type SectionHeaderProps = {
  activeGroupSlug: string;
  isEditingDisabled: boolean;
};

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0.15em 1.5em;
  margin-bottom: 2em;
  background-color: ${({ theme }) => theme.colors.grayf4};
  border: 1px solid ${({ theme }) => theme.colors.grayddd};
  border-radius: 0.3rem;
`;

const SectionNameDisplay = styled('h2')`
  flex-grow: 1;
  overflow-wrap: anywhere;
  ${header2}
`;

export function SectionHeader({
  activeGroupSlug,
  isEditingDisabled,
}: SectionHeaderProps) {
  const { formTemplate } = useFormTemplateContext();
  const groups = formTemplate?.groups || [];

  const activeGroup = useMemo(
    () => formTemplate.groups.find((group) => group.slug === activeGroupSlug),
    [formTemplate, activeGroupSlug],
  );
  const activeGroupName = activeGroup?.name || '';

  const [isEditSectionModalOpen, openEditSectionModal, closeEditSectionModal] =
    useOpenClose();

  const [isCopySectionModalOpen, openCopySectionModal, closeCopySectionModal] =
    useOpenClose();

  const [
    isDeleteSectionModalOpen,
    openDeleteSectionModal,
    closeDeleteSectionModal,
  ] = useOpenClose();

  const actionButtons: MenuOption[] = useMemo(
    () => [
      {
        displayName: 'Edit',
        onClick: openEditSectionModal,
        disabled: isEditingDisabled,
      },
      {
        displayName: 'Copy',
        onClick: openCopySectionModal,
        disabled: isEditingDisabled,
      },
      {
        displayName: 'Delete',
        onClick: openDeleteSectionModal,
        disabled: groups.length === 1 || isEditingDisabled,
        color: 'danger',
        tooltip:
          groups.length === 1 ? 'Forms must have at least one section.' : '',
      },
    ],
    [
      openEditSectionModal,
      isEditingDisabled,
      openCopySectionModal,
      openDeleteSectionModal,
      groups.length,
    ],
  );

  const currentSectionFormValues = {
    name: activeGroupName,
    isDuplicable: !!activeGroup?.isDuplicable,
    isStencilProtected:
      !!activeGroup?.isDuplicable && !!activeGroup?.isStencilProtected,
  };

  return (
    <Container>
      <SectionNameDisplay>{activeGroupName}</SectionNameDisplay>
      <ActionMenu
        color="secondary"
        options={actionButtons}
        isDisabled={isEditingDisabled}
      />
      {!!isEditSectionModalOpen && (
        <EditSectionModal
          isOpen={!!isEditSectionModalOpen}
          onClose={closeEditSectionModal}
          sectionFormValues={currentSectionFormValues}
        />
      )}
      {!!isCopySectionModalOpen && (
        <CopySectionModal
          isOpen={!!isCopySectionModalOpen}
          onClose={closeCopySectionModal}
          copiedSectionFormValues={currentSectionFormValues}
        />
      )}
      {!!isDeleteSectionModalOpen && (
        <DeleteSectionModal
          isOpen={!!isDeleteSectionModalOpen}
          onClose={closeDeleteSectionModal}
          activeGroupName={activeGroupName}
        />
      )}
    </Container>
  );
}
